import mapboxgl from "mapbox-gl"
import { MAP_STYLES } from "@/components/constants"
import { BASE_MAP_STYLES } from "@/components/map/baseMapStyles"
import { addCadastre, PARCEL_SOURCE_LAYER } from "@/components/map/cadastreLayer"
import { addFloodableZones, addWetlands } from "@/components/map/environmentLayers"
import { addTransitLines, addTransitStations, addTransitStationsLabels } from "@/components/map/transitLayers"
import { tileRequestHeaders } from "@/utils/tileRequestHeaders"

const MAP_WIDTH = 1100
const MAP_HEIGHT = 680
const DEFAULT_MAP_ZOOM = 15
const CAPTURE_TIMEOUT_MS = 20000

export enum ParcelMapView {
	Location = "location",
	Transportation = "transportation",
	Wetlands = "wetlands",
	FloodableZones = "floodable-zones",
}

function mapZoom(): number {
	const configuredZoom = Number(import.meta.env.VITE_PARCEL_PDF_MAP_ZOOM)
	return configuredZoom >= 13 && configuredZoom <= 22 ? configuredZoom : DEFAULT_MAP_ZOOM
}

interface ParcelMapSnapshotOptions {
	parcelId: string
	latitude: number
	longitude: number
	apiToken: string | null | undefined
	view?: ParcelMapView
}

function parcelPin(): ImageData {
	const canvas = document.createElement("canvas")
	canvas.width = 72
	canvas.height = 96
	const context = canvas.getContext("2d")
	if (!context) throw new Error("Could not draw the parcel marker")

	context.beginPath()
	context.moveTo(36, 90)
	context.bezierCurveTo(28, 76, 10, 56, 10, 34)
	context.arc(36, 34, 26, Math.PI, 0)
	context.bezierCurveTo(62, 56, 44, 76, 36, 90)
	context.closePath()
	context.fillStyle = "#174f31"
	context.fill()
	context.strokeStyle = "#ffffff"
	context.lineWidth = 4
	context.stroke()
	context.beginPath()
	context.arc(36, 34, 9, 0, Math.PI * 2)
	context.fillStyle = "#ffffff"
	context.fill()
	return context.getImageData(0, 0, canvas.width, canvas.height)
}

export async function captureParcelMap(options: ParcelMapSnapshotOptions): Promise<string> {
	const { parcelId, latitude, longitude, apiToken, view = ParcelMapView.Location } = options
	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
		throw new Error("Parcel coordinates are unavailable")
	}

	const container = document.createElement("div")
	Object.assign(container.style, {
		position: "fixed",
		left: `-${MAP_WIDTH + 100}px`,
		top: "0",
		width: `${MAP_WIDTH}px`,
		height: `${MAP_HEIGHT}px`,
		pointerEvents: "none",
	})
	document.body.appendChild(container)

	let map: mapboxgl.Map | undefined
	try {
		map = new mapboxgl.Map({
			container,
			accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
			style: BASE_MAP_STYLES[MAP_STYLES.SIMPLE],
			center: [longitude, latitude],
			zoom: mapZoom(),
			bearing: 0,
			pitch: 0,
			interactive: false,
			attributionControl: false,
			preserveDrawingBuffer: true,
			transformRequest: url => ({ url, headers: tileRequestHeaders({ url, apiToken }) }),
		})
		const snapshotMap = map

		await new Promise<void>((resolve, reject) => {
			let mapError: Error | undefined
			let finished = false
			const timeout = window.setTimeout(
				() => settle(mapError ?? new Error("Map snapshot timed out")),
				CAPTURE_TIMEOUT_MS
			)
			const onError = (event: mapboxgl.ErrorEvent) => {
				mapError = new Error(event.error.message)
			}
			const settle = (error?: Error) => {
				if (finished) return
				finished = true
				window.clearTimeout(timeout)
				snapshotMap.off("error", onError)
				if (error) reject(error)
				else resolve()
			}

			snapshotMap.on("error", onError)
			snapshotMap.once("style.load", () => {
				try {
					const baseUrl = import.meta.env.VITE_TILES_URL_DAILY
					addCadastre(snapshotMap, {
						baseUrl,
						sourceName: "cadastre-source",
						layerName: "cadastre",
						visibleLayers: ["cadastre"],
					})
					if (view === ParcelMapView.Transportation) {
						const visibleLayers = ["transit-lines", "transit-stations"]
						addTransitLines(snapshotMap, {
							baseUrl,
							visibleLayers,
							sourceName: "transit-lines-source",
							layerName: "transit-lines",
						})
						addTransitStations(snapshotMap, {
							baseUrl,
							visibleLayers,
							sourceName: "transit-stations-source",
							layerName: "transit-stations",
						})
						addTransitStationsLabels(snapshotMap, {
							baseUrl,
							visibleLayers,
							sourceName: "transit-stations_labels-source",
							layerName: "transit-stations_labels",
						})
					}
					if (view === ParcelMapView.Wetlands) {
						addWetlands(snapshotMap, {
							baseUrl,
							visibleLayers: ["wetlands"],
							sourceName: "wetlands-source",
							layerName: "wetlands",
						})
					}
					if (view === ParcelMapView.FloodableZones) {
						addFloodableZones(snapshotMap, {
							baseUrl,
							visibleLayers: ["floodable-zones"],
							sourceName: "floodable-zones-source",
							layerName: "floodable-zones",
						})
					}
					snapshotMap.addLayer({
						id: "parcel-report-highlight",
						type: "fill",
						source: "cadastre-source",
						"source-layer": PARCEL_SOURCE_LAYER,
						filter: ["==", ["to-string", ["get", "parcel_id"]], parcelId],
						paint: {
							"fill-color": "#0d6349",
							"fill-opacity": 0.25,
							"fill-outline-color": "#0d6349",
						},
					})
					snapshotMap.addSource("parcel-report-center", {
						type: "geojson",
						data: { type: "Point", coordinates: [longitude, latitude] },
					})
					snapshotMap.addImage("parcel-report-pin", parcelPin(), { pixelRatio: 2 })
					snapshotMap.addLayer({
						id: "parcel-report-marker",
						type: "symbol",
						source: "parcel-report-center",
						layout: {
							"icon-image": "parcel-report-pin",
							"icon-anchor": "bottom",
							"icon-allow-overlap": true,
						},
					})
					snapshotMap.once("idle", () => settle())
				} catch (error) {
					settle(error instanceof Error ? error : new Error(String(error)))
				}
			})
		})

		return snapshotMap.getCanvas().toDataURL("image/png")
	} finally {
		map?.remove()
		container.remove()
	}
}
